/**
 * StudioHistoryPanel — the "track my prompts, tweaks & preferences" surface
 * (Tweak Mode phase 3+4).
 *
 * HISTORY reads the existing edit journal (no new storage): one row per applied
 * tweak — canvas drag, inspector change, or an AI tweak from the popover/chat
 * (source "chat", label = the agent's explanation, i.e. the readable prompt
 * result). Undoable tail entries get "Undo to here" (restores the design as of
 * that step, stopping at any baked/promoted step, which are permanent).
 *
 * PREFERENCES reuses the existing channel-promotion ("Save as my style"): the
 * persistent home for "make every future video look like this". Once saved, new
 * clips on the channel render from this design by construction.
 */
import { Check, ChevronDown, ChevronRight, History, Loader2, Move, Scissors, SlidersHorizontal, Sparkles, Undo2 } from "lucide-react";
import { useCallback, useState } from "react";
import type { EditJournalEntry, EditSource } from "../../../src/lib/clipEditState.ts";

const ACCENT = "#FFD500";

function fmtTime(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

const SOURCE_META: Record<EditSource, { icon: typeof Move; label: string; color: string }> = {
  canvas: { icon: Move, label: "drag", color: "var(--ql-fg-subtle)" },
  inspector: { icon: SlidersHorizontal, label: "inspector", color: "var(--ql-fg-subtle)" },
  chat: { icon: Sparkles, label: "AI tweak", color: ACCENT },
  // The measured fit gate split a caption to keep it inside the frame.
  fit: { icon: Scissors, label: "fit", color: "var(--ql-fg-subtle)" },
};

export interface StudioHistoryPanelProps {
  journal: EditJournalEntry[];
  /** Undo every entry AFTER `index` (tail only — stops at baked/promoted). */
  onUndoTo: (index: number) => void;
  // Preferences (reuses the existing channel promotion):
  channelSlug: string | null;
  canPromote: boolean;
  promoting: boolean;
  designPromoted: boolean;
  onPromote: () => void;
  promoteNotice: string | null;
}

export function StudioHistoryPanel({
  journal,
  onUndoTo,
  channelSlug,
  canPromote,
  promoting,
  designPromoted,
  onPromote,
  promoteNotice,
}: StudioHistoryPanelProps): React.JSX.Element {
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem("ql-studio-history-open") === "1"; } catch { return false; }
  });
  const toggle = useCallback(() => {
    setOpen((o) => {
      const v = !o;
      try { localStorage.setItem("ql-studio-history-open", v ? "1" : "0"); } catch { /* private mode */ }
      return v;
    });
  }, []);

  // Index of the last permanent (baked/promoted) step — nothing at or before it
  // can be undone.
  let lastCommittedIdx = -1;
  journal.forEach((e, i) => {
    if (e.bakedAt !== undefined || e.promotedAt !== undefined) lastCommittedIdx = i;
  });

  const rows = journal.map((e, i) => ({ e, i })).reverse();

  return (
    <section
      style={{
        marginBottom: 16,
        border: "1px solid rgba(255,255,255,.08)",
        borderRadius: 14,
        background: "rgba(255,255,255,.015)",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        style={{
          display: "flex", alignItems: "center", gap: 10, width: "100%",
          padding: "12px 14px", cursor: "pointer", textAlign: "left",
          fontFamily: "inherit", background: "transparent", border: "none", color: "var(--ql-fg)",
        }}
      >
        <History size={15} color={ACCENT} />
        <span style={{ fontSize: 13, fontWeight: 700 }}>History &amp; preferences</span>
        <span style={{ fontSize: 11.5, color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)" }}>
          {journal.length} edit{journal.length === 1 ? "" : "s"}
        </span>
        <span style={{ marginLeft: "auto", color: "var(--ql-fg-muted)", display: "flex" }}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
      </button>

      {open && (
        <div style={{ padding: "0 14px 14px" }}>
          {journal.length === 0 ? (
            <div style={{ fontSize: 12.5, lineHeight: 1.6, color: "var(--ql-fg-subtle)", padding: "6px 2px 12px" }}>
              No edits yet. Click any part of the video and describe a tweak, or drag
              elements directly — every change lands here, and you can step back to any point.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: channelSlug ? 14 : 2 }}>
              {rows.map(({ e, i }) => {
                const meta = SOURCE_META[e.source];
                const Icon = meta.icon;
                const isLatest = i === journal.length - 1;
                const committed = e.bakedAt !== undefined || e.promotedAt !== undefined;
                const canUndoToHere = i > lastCommittedIdx && !isLatest;
                return (
                  <div
                    key={`${e.ts}-${i}`}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "7px 8px", borderRadius: 9,
                      background: isLatest ? "rgba(255,213,0,.05)" : "transparent",
                    }}
                  >
                    <span
                      style={{
                        width: 24, height: 24, borderRadius: 7, flexShrink: 0,
                        display: "grid", placeItems: "center",
                        background: "rgba(255,255,255,.04)", color: meta.color,
                      }}
                      title={meta.label}
                    >
                      <Icon size={12} />
                    </span>
                    <span style={{ minWidth: 0, flex: 1, display: "grid", gap: 1 }}>
                      <span
                        style={{
                          fontSize: 12.5, lineHeight: 1.4, color: "var(--ql-fg)",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}
                        title={e.label}
                      >
                        {e.label}
                      </span>
                      <span style={{ fontSize: 10, color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)", display: "flex", gap: 7, alignItems: "center" }}>
                        {fmtTime(e.ts)}
                        {e.bakedAt !== undefined && <Badge text="baked into captions" tone="neutral" />}
                        {e.promotedAt !== undefined && <Badge text="saved to channel" tone="good" />}
                        {e.scope === "channel-pending" && e.promotedAt === undefined && <Badge text="channel-pending" tone="warn" />}
                        {isLatest && !committed && <Badge text="current" tone="accent" />}
                      </span>
                    </span>
                    {canUndoToHere && (
                      <button
                        type="button"
                        onClick={() => onUndoTo(i)}
                        title="Undo everything after this step"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0,
                          fontSize: 10.5, fontWeight: 600, color: "var(--ql-fg-muted)",
                          background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)",
                          borderRadius: 999, padding: "3px 9px", cursor: "pointer", fontFamily: "inherit",
                        }}
                      >
                        <Undo2 size={10} /> Undo to here
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {channelSlug && (
            <div
              style={{
                borderTop: "1px solid rgba(255,255,255,.07)", paddingTop: 13,
                display: "grid", gap: 9,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 800, color: "var(--ql-fg)" }}>
                Your style for {channelSlug}
              </div>
              <div style={{ fontSize: 11.5, lineHeight: 1.55, color: "var(--ql-fg-muted)", maxWidth: 620 }}>
                Save this look as your channel style and <b>every new video</b> on {channelSlug} starts
                here — centered, sized, colored exactly like this. Re-save anytime to change it.
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={onPromote}
                  disabled={!canPromote || promoting}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 13px", borderRadius: 10,
                    cursor: canPromote && !promoting ? "pointer" : "default", fontFamily: "inherit",
                    fontSize: 12, fontWeight: 800,
                    border: "1px solid rgba(255,213,0,.55)", color: "var(--ql-yellow)", background: "rgba(255,213,0,.11)",
                    opacity: canPromote || promoting ? 1 : 0.5,
                  }}
                  title={canPromote ? `Lock this into ${channelSlug} — every future video renders with it` : "Make a design change first, then save it as your style"}
                >
                  {promoting ? <Loader2 size={13} className="ql-spin" /> : <Check size={13} />}
                  {promoting ? "Saving your style…" : "Save as my style"}
                </button>
                {!canPromote && !promoting && (
                  <span style={{ fontSize: 11, color: "var(--ql-fg-subtle)" }}>
                    {designPromoted ? "✓ Saved — this is your channel’s style." : "Make a design change to enable this."}
                  </span>
                )}
              </div>
              {promoteNotice && (
                <div style={{ fontSize: 11, lineHeight: 1.5, color: promoteNotice.startsWith("Couldn't") ? "var(--ql-danger)" : "rgba(99,226,183,.9)" }}>
                  {promoteNotice}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

type BadgeTone = "neutral" | "good" | "warn" | "accent";

function Badge({ text, tone }: { text: string; tone: BadgeTone }): React.JSX.Element {
  const palette: Record<BadgeTone, { fg: string; bg: string; bd: string }> = {
    neutral: { fg: "var(--ql-fg-muted)", bg: "rgba(255,255,255,.05)", bd: "rgba(255,255,255,.12)" },
    good: { fg: "#63E2B7", bg: "rgba(99,226,183,.10)", bd: "rgba(99,226,183,.25)" },
    warn: { fg: "var(--ql-yellow)", bg: "rgba(255,213,0,.08)", bd: "rgba(255,213,0,.25)" },
    accent: { fg: "var(--ql-yellow)", bg: "rgba(255,213,0,.12)", bd: "rgba(255,213,0,.35)" },
  };
  const c = palette[tone];
  return (
    <span
      style={{
        fontSize: 9, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
        color: c.fg, background: c.bg, border: `1px solid ${c.bd}`, borderRadius: 999, padding: "1px 6px",
      }}
    >
      {text}
    </span>
  );
}
